<template>
    <Modal size="md" allow-overflow @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Export to Lettr</div>
        </template>

        <template #body>
            <div class="overflow-visible p-6">
                <form>
                    <SelectInput
                        v-model="state.purpose"
                        placeholder="Choose purpose"
                        :data="purposes"
                        label="Purpose"
                        required
                        :error="getError('purpose')"
                        :disabled="loading"
                        @blurred="v$.purpose.$touch()"
                    />

                    <SelectInput
                        v-model="folderId"
                        class="mt-6"
                        :placeholder="folderPlaceholder"
                        :data="folders"
                        label="Folder"
                        :error="foldersError"
                        :disabled="loading || foldersLoading || folders.length === 0"
                    />

                    <p class="mt-2 text-sm text-gray-500">
                        Lettr keeps campaign and transactional templates apart,
                        each with its own folders.
                    </p>
                </form>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || foldersLoading || v$.$invalid"
                    @click="exportToLettr"
                >
                    Export
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, computed, reactive, ref, toRef } from 'vue';
import { IIntegration, ILettr } from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ISelectable from '@/types/Atomic/ISelectable';
import useLettrFolders from '../composables/useLettrFolders';
import { LettrPurpose, lettrEmailsUrl } from '@/utils/lettrUrls';

const emit = defineEmits(['close']);

const props = defineProps({
    integration: {
        required: true,
        type: Object as PropType<IIntegration<ILettr>>,
    },
    templateId: {
        required: true,
        type: Number,
    },
    templateName: {
        required: true,
        type: String,
    },
});

const state = reactive({
    purpose: 'campaign',
});

const rules = {
    purpose: {
        required,
    },
};

const v$ = useVuelidate(rules, state);

const loading = ref(false);

const purposes = ref<ISelectable[]>([
    {
        title: 'Campaign',
        value: 'campaign',
    },
    {
        title: 'Transactional',
        value: 'transactional',
    },
]);

const {
    folders,
    folderId,
    loading: foldersLoading,
    error: foldersError,
    load: loadFolders,
} = useLettrFolders(
    toRef(() => props.integration.id),
    toRef(state, 'purpose')
);

loadFolders();

const folderPlaceholder = computed(() => {
    if (foldersLoading.value) {
        return 'Loading folders…';
    }

    return folders.value.length === 0
        ? 'No folders in Lettr'
        : 'Choose folder';
});

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

/**
 * Opens the Lettr list the template landed in, rather than Lettr's front page:
 * the two purposes are separate modules there and only one of them holds it.
 */
const openInLettr = () => {
    window.open(
        lettrEmailsUrl(state.purpose as LettrPurpose, 'single_export'),
        '_blank',
        'noopener'
    );
};

const exportToLettr = async () => {
    loading.value = true;

    try {
        const response = await axios.post(
            route('templates.exports.lettr', {
                template: props.templateId,
            }),
            {
                integration_id: props.integration.id,
                purpose: state.purpose,
                folder_id: folderId.value === '' ? null : folderId.value,
            }
        );

        if (!response.data.success) {
            create({
                title: 'Unsuccessful export',
                text: 'Template has not been exported to Lettr.',
                type: 'error',
            });

            return;
        }

        // Reported straight off the create. Lettr finishes rendering the HTML
        // and migrating the images after it answers, but the toast only links
        // to the list it landed in rather than to the template itself, so
        // there is nothing here that needs the template to be prepared first.
        create({
            title: 'Successful export',
            text: 'Template has been exported to Lettr.',
            type: 'success',
            button: true,
            buttonText: 'Open in Lettr',
            onButtonClick: openInLettr,
        });
        emit('close');
    } catch (e) {
        create({
            title: 'Unsuccessful export',
            text:
                axios.isAxiosError(e) && e.response?.data?.message
                    ? e.response.data.message
                    : 'Template has not been exported to Lettr.',
            type: 'error',
        });
    } finally {
        loading.value = false;
    }
};
</script>
